// Design group dashboard, naming, scope and membership changes.
import {
  ForgeDesignGroupDifferentiator,
  ForgeDesignGroupDifferentiatorUpdateResponse,
  ForgeDesignGroupNameUpdateResponse,
  ForgeDesignGroupsDashboardResponse,
  ForgeDesignGroupSearchResponse,
  ForgeDesignGroupsRefreshRequest,
  ForgeDesignGroupsScopeChangeRequest,
  ModifyForgeDesignGroupsRequest,
  ModifyForgeDesignGroupsResponse,
} from '../../types/designGroups';
import {
  ForgeReviewSort,
  ForgeReviewStatusUpdateRequest,
  ForgeReviewStatusUpdateResponse,
} from '../../types/designs';
import { forgeRequest } from '../forgeRequest';
import { ApiReviewStatusUpdateResponse, normalizeReviewStatusUpdateResponse } from './designs';
import { FORGE_DASHBOARDS_API_URL, FORGE_DESIGN_GROUPS_API_URL } from './endpoints';

export async function fetchDesignGroupsDashboard(
  token: string,
  filters?: {
    chainId?: number | null;
    scope?: string | null;
    query?: string | null;
    updatedBy?: string | null;
    updatedByValues?: string[] | null;
    reviewSort?: ForgeReviewSort | null;
    page?: number;
    limit?: number;
  }
): Promise<ForgeDesignGroupsDashboardResponse> {
  const params = new URLSearchParams();
  if (filters?.chainId) params.set('chain_id', String(filters.chainId));
  if (filters?.scope) params.set('scope', filters.scope);
  if (filters?.query) params.set('query', filters.query);
  if (filters?.updatedBy) params.set('updated_by', filters.updatedBy);
  filters?.updatedByValues?.forEach((value) => params.append('updated_by_values', value));
  if (filters?.reviewSort) params.set('review_sort', filters.reviewSort);
  if (filters?.page) params.set('page', String(filters.page));
  if (filters?.limit) params.set('limit', String(filters.limit));
  const query = params.toString();
  const requestKey = `${token}:${query}`;
  return forgeRequest<ForgeDesignGroupsDashboardResponse>(
    token,
    `${FORGE_DASHBOARDS_API_URL}/design-groups${query ? `?${query}` : ''}`,
    'Error fetching Forge design groups dashboard',
    { dedupeKey: ['fetchDesignGroupsDashboard', requestKey] }
  );
}

export async function refreshDesignGroupsDashboard(
  token: string,
  request: ForgeDesignGroupsRefreshRequest
): Promise<ForgeDesignGroupsDashboardResponse> {
  return forgeRequest<ForgeDesignGroupsDashboardResponse>(
    token,
    `${FORGE_DASHBOARDS_API_URL}/design-groups/refresh`,
    'Error refreshing Forge design groups dashboard',
    { method: 'POST', body: request }
  );
}

export async function updateDesignGroupScope(
  token: string,
  groupId: string,
  request: ForgeDesignGroupsScopeChangeRequest
): Promise<ForgeDesignGroupsDashboardResponse> {
  return forgeRequest<ForgeDesignGroupsDashboardResponse>(
    token,
    `${FORGE_DASHBOARDS_API_URL}/design-groups/${groupId}/scope`,
    'Error changing Forge design group type',
    { method: 'PATCH', body: request }
  );
}

export async function updateDesignGroupName(
  token: string,
  groupId: string,
  name: string | null
): Promise<ForgeDesignGroupNameUpdateResponse> {
  return forgeRequest<ForgeDesignGroupNameUpdateResponse>(
    token,
    `${FORGE_DESIGN_GROUPS_API_URL}/${groupId}/name`,
    'Error updating Forge design group name',
    { method: 'PATCH', body: { name } }
  );
}

export async function updateDesignGroupDifferentiator(
  token: string,
  groupId: string,
  differentiator: ForgeDesignGroupDifferentiator | null
): Promise<ForgeDesignGroupDifferentiatorUpdateResponse> {
  return forgeRequest<ForgeDesignGroupDifferentiatorUpdateResponse>(
    token,
    `${FORGE_DESIGN_GROUPS_API_URL}/${groupId}/differentiator`,
    'Error updating Forge design group differentiator',
    { method: 'PATCH', body: { differentiator } }
  );
}

export async function updateDesignGroupReviewStatus(
  token: string,
  groupId: string,
  request: ForgeReviewStatusUpdateRequest
): Promise<ForgeReviewStatusUpdateResponse> {
  return normalizeReviewStatusUpdateResponse(
    await forgeRequest<ApiReviewStatusUpdateResponse>(
      token,
      `${FORGE_DESIGN_GROUPS_API_URL}/${groupId}/review`,
      'Error updating Forge design group review status',
      { method: 'PATCH', body: request }
    ),
    groupId,
    request.isReviewed
  );
}

export async function searchDesignGroups(
  token: string,
  chainId: number,
  query: string,
  page = 1,
  limit = 20
): Promise<ForgeDesignGroupSearchResponse> {
  const params = new URLSearchParams({
    chain_id: String(chainId),
    query,
    page: String(page),
    limit: String(limit),
  });
  return forgeRequest<ForgeDesignGroupSearchResponse>(
    token,
    `${FORGE_DESIGN_GROUPS_API_URL}/search?${params}`,
    'Error searching Forge design groups'
  );
}

export async function modifyDesignGroups(
  token: string,
  request: ModifyForgeDesignGroupsRequest
): Promise<ModifyForgeDesignGroupsResponse> {
  return forgeRequest<ModifyForgeDesignGroupsResponse>(
    token,
    `${FORGE_DESIGN_GROUPS_API_URL}`,
    'Error updating Forge design groups',
    { method: 'POST', body: request }
  );
}
